import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';
import type { EngineToDebugUiEvent } from '../types';
import { logger, loggerEventBus, type UserLogEntry } from '../../core/Logger';
import { DockingManager } from '../DockingManager';
import type { DockingLayout } from '../DockingManager';
import { DEBUG_UI_ACTION_EVENT, type DebugUiUiAction } from '../uiActions';
import { availableMaps } from '../../config/maps';
import './himum-debug-window';
import './dock-region';
import './dock-tab-group';
import './dock-tab';

type WindowId = 'debug' | 'post' | 'lighting' | 'assets' | 'scene';
type PanelId = 'sidebar' | 'scene' | 'cameras' | 'console' | 'help' | 'timeline' | 'combat';

/**
 * Hotkey definitions for the debug HUD.
 * All debug-related hotkeys are centralized here for isolation from the engine.
 */
interface HotkeyDefinition {
  key: string;
  modifiers?: ('ctrl' | 'shift' | 'alt')[];
  description: string;
  category: string;
  action: (hud: HimumDebugHud) => void;
  /** If true, only active when HUD is visible */
  requiresHudVisible?: boolean;
  /** If true, only active when a specific panel is open */
  requiresPanel?: PanelId;
}

const HOTKEY_DEFINITIONS: HotkeyDefinition[] = [
  // HUD visibility
  { key: 'F8', description: 'Toggle Debug HUD', category: 'HUD', action: (h) => h.toggleHud() },
  
  // Panel toggles (Ctrl+number)
  { key: '1', modifiers: ['ctrl'], description: 'Toggle Sidebar Panel', category: 'Panels', action: (h) => h.togglePanel('sidebar'), requiresHudVisible: true },
  { key: '2', modifiers: ['ctrl'], description: 'Toggle Scene Inspector', category: 'Panels', action: (h) => h.togglePanel('scene'), requiresHudVisible: true },
  { key: '3', modifiers: ['ctrl'], description: 'Toggle Console', category: 'Panels', action: (h) => h.togglePanel('console'), requiresHudVisible: true },
  { key: '4', modifiers: ['ctrl'], description: 'Toggle Help', category: 'Panels', action: (h) => h.togglePanel('help'), requiresHudVisible: true },
  { key: '5', modifiers: ['ctrl'], description: 'Toggle Animation Timeline', category: 'Panels', action: (h) => h.togglePanel('timeline'), requiresHudVisible: true },
  { key: '6', modifiers: ['ctrl'], description: 'Toggle Combat Panel', category: 'Panels', action: (h) => h.togglePanel('combat'), requiresHudVisible: true },
  
  // Quick toggles (handled by HUD)
  { key: 'F7', description: 'Toggle Debug Rendering', category: 'Debug', action: (h) => h.toggleDebugRendering() },
  
  // Game time controls
  { key: 'p', description: 'Toggle Pause', category: 'Time', action: (h) => h.toggleGamePause() },
  { key: 'l', description: 'Toggle Slow-Motion', category: 'Time', action: (h) => h.toggleSlowMotion() },
];

/** Hotkeys documented but handled elsewhere (engine/overlay) */
const EXTERNAL_HOTKEY_DOCS: Array<{ key: string; modifiers?: string[]; description: string; category: string }> = [
  // Camera (handled in main.ts)
  { key: 'F6', description: 'Toggle Free-roam Camera', category: 'Camera' },
  
  // Assets (handled in main.ts)
  { key: 'F7', modifiers: ['shift'], description: 'Log Asset Overview to Console', category: 'Assets' },
  
  // Game controls
  { key: '1/2/3', description: 'Switch party leader', category: 'Game' },
  { key: 'A/D or ←/→', description: 'Move character', category: 'Game' },
  { key: 'Space', description: 'Jump', category: 'Game' },
  { key: 'F', description: 'Attack', category: 'Game' },
];

interface StoredHudState {
  version: 1;
  windows: Record<WindowId, { x: number; y: number; width: number; height: number; visible: boolean }>;
  panels: Record<PanelId, boolean>;
  favorites: string[];
  options: {
    splineDebug: boolean;
    playerDebug: boolean;
    weaponMountDebug: boolean;
    debugRendering: boolean;

    pixelation: boolean;
    pixelSize: number;
    pixelAnimate: boolean;
    godRays: boolean;

    chromaticAberration: boolean;
    chromaticAberrationAmount: number;
    chromaticAberrationRadialIntensity: number;

    fullBright: boolean;
  };
  layout: {
    leftWidth: number;
    rightWidth: number;
    leftCollapsed: boolean;
    rightCollapsed: boolean;
    bottomCollapsed?: boolean;
  };
}

const STORAGE_KEY = 'himum.debugHud.v1';

const DEFAULT_STATE: StoredHudState = {
  version: 1,
  windows: {
    debug: { x: 20, y: 20, width: 420, height: 380, visible: true },
    lighting: { x: 20, y: 420, width: 420, height: 220, visible: true },
    post: { x: 980, y: 20, width: 420, height: 360, visible: true },
    assets: { x: 980, y: 400, width: 520, height: 360, visible: true },
    scene: { x: 520, y: 20, width: 420, height: 420, visible: true },
  },
  panels: {
    sidebar: true,
    scene: true,
    cameras: false,
    console: false,
    help: false,
    timeline: true,
    combat: false,
  },
  favorites: [],
  options: {
    splineDebug: false,
    playerDebug: false,
    weaponMountDebug: false,
    debugRendering: false,

    pixelation: false,
    pixelSize: 1,
    pixelAnimate: false,
    godRays: false,

    chromaticAberration: false,
    chromaticAberrationAmount: 12,
    chromaticAberrationRadialIntensity: 1.0,

    fullBright: false,
  },
  layout: {
    leftWidth: 440,
    rightWidth: 560,
    leftCollapsed: false,
    rightCollapsed: false,
    bottomCollapsed: false,
  },
};

const HUD_VISIBLE_STORAGE_KEY = 'himum-debug-hud-visible';

@customElement('himum-debug-hud')
export class HimumDebugHud extends LitElement {
  static styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 9999;
      pointer-events: none;
    }

    /* Top bar for panel toggles */
    .top-bar {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 36px;
      background: rgba(20, 20, 25, 0.95);
      border-bottom: 1px solid rgba(255, 255, 255, 0.15);
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 0 12px;
      pointer-events: auto;
      z-index: 10;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 12px;
      color: #eee;
    }

    .top-bar-title {
      font-weight: 600;
      opacity: 0.9;
      margin-right: 8px;
    }

    .top-bar-divider {
      width: 1px;
      height: 20px;
      background: rgba(255, 255, 255, 0.2);
      margin: 0 4px;
    }

    .panel-btn {
      padding: 4px 10px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(255, 255, 255, 0.05);
      color: #ccc;
      cursor: pointer;
      border-radius: 4px;
      font-size: 11px;
      transition: all 0.15s;
    }

    .panel-btn:hover {
      background: rgba(255, 255, 255, 0.12);
      color: #fff;
    }

    .panel-btn.active {
      background: rgba(100, 160, 255, 0.25);
      border-color: rgba(100, 160, 255, 0.5);
      color: #fff;
    }

    .top-bar-spacer {
      flex: 1;
    }

    .top-bar-hint {
      opacity: 0.6;
      font-size: 10px;
    }

    .map-selector-inline {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
    }
    
    .map-label {
      font-size: 14px;
    }
    
    .map-dropdown {
      font-family: 'Consolas', 'Monaco', monospace;
      font-size: 11px;
      background: rgba(20, 25, 35, 0.9);
      color: rgba(200, 230, 255, 0.95);
      border: 1px solid rgba(100, 160, 255, 0.4);
      padding: 4px 8px;
      border-radius: 3px;
      cursor: pointer;
      min-width: 200px;
      max-width: 300px;
      transition: all 0.2s;
    }
    
    .map-dropdown:hover {
      background: rgba(30, 35, 45, 0.95);
      border-color: rgba(120, 180, 255, 0.6);
    }
    
    .map-dropdown:focus {
      outline: none;
      border-color: rgba(140, 200, 255, 0.8);
      box-shadow: 0 0 4px rgba(100, 160, 255, 0.3);
    }
    
    .map-dropdown option {
      background: rgba(20, 25, 35, 1);
      color: rgba(200, 230, 255, 0.95);
      padding: 4px;
    }
    
    .map-reload-btn {
      background: rgba(100, 200, 100, 0.3);
      border: 1px solid rgba(100, 200, 100, 0.5);
      border-radius: 3px;
      color: white;
      cursor: pointer;
      padding: 2px 6px;
      font-size: 11px;
      transition: all 0.15s;
    }
    
    .map-reload-btn:hover {
      background: rgba(100, 200, 100, 0.5);
      border-color: rgba(100, 200, 100, 0.7);
    }

    .dock {
      position: absolute;
      top: 46px; /* Below top bar */
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      gap: 0; /* Removed gap to allow resizers to sit flush */
      padding: 0; /* Removed padding to allow full edge docking */
      pointer-events: none;
      box-sizing: border-box;
    }

    .left {
      width: var(--left-width, 440px);
      flex-shrink: 0;
      position: relative;
      transition: width 0.2s ease-out;
    }

    .left.collapsed {
      width: 0 !important;
      min-width: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
      background: transparent;
      backdrop-filter: none;
      pointer-events: none;
      border: none;
      overflow: hidden !important;
      visibility: hidden;
    }

    .expand-btn {
      position: absolute;
      z-index: 1000;
      width: 32px;
      height: 32px;
      background: rgba(20, 20, 25, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: #ccc;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      pointer-events: auto;
      font-size: 18px;
      line-height: 1;
    }

    .expand-btn.left-expand {
      left: 0;
      top: 4px;
      border-radius: 0 4px 4px 0;
    }

    .right {
      width: var(--right-width, 560px);
      flex-shrink: 0;
      position: relative;
      transition: width 0.2s ease-out;
    }

    .right.collapsed {
      width: 0 !important;
      min-width: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
      background: transparent;
      backdrop-filter: none;
      pointer-events: none;
      border: none;
      overflow: hidden !important;
      visibility: hidden;
    }

    .expand-btn.right-expand {
      right: 0;
      top: 4px;
      border-radius: 4px 0 0 4px;
    }

    .left,
    .right {
      pointer-events: auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
      background: rgba(20, 20, 25, 0.8); /* Slight background for visibility */
      backdrop-filter: blur(4px);
    }

    .left {
      border-right: 1px solid rgba(255, 255, 255, 0.1);
    }

    .right {
      border-left: 1px solid rgba(255, 255, 255, 0.1);
    }

    .resizer {
      width: 6px;
      cursor: col-resize;
      background: transparent;
      position: absolute;
      top: 0;
      bottom: 0;
      z-index: 100;
      transition: background 0.2s;
    }

    .resizer:hover, .resizer.resizing {
      background: rgba(100, 160, 255, 0.5);
    }

    .left .resizer {
      right: -3px;
    }

    .right .resizer {
      left: -3px;
    }
    
    /* Hide right resizer on right dock if it exists */
    .right .resizer:nth-child(2) {
      display: none;
    }

    .collapse-btn {
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #aaa;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .collapse-btn:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.05);
    }

    .expand-btn {
      width: 32px;
      height: 32px;
      background: rgba(20, 20, 25, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: #ccc;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      pointer-events: auto;
      margin-top: 4px;
      border-radius: 0 4px 4px 0;
      font-size: 18px;
      line-height: 1;
    }

    .expand-btn.bottom-expand {
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      border-radius: 4px 4px 0 0;
    }

    .expand-btn:hover {
      background: rgba(40, 40, 50, 0.9);
      color: white;
    }

    .bottom {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      height: var(--bottom-height, 250px);
      background: rgba(20, 20, 25, 0.8);
      backdrop-filter: blur(4px);
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      flex-direction: column;
      transition: height 0.2s ease-out;
      z-index: 1000;
      pointer-events: auto;
    }

    .bottom.collapsed {
      height: 0 !important;
      min-height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
      background: transparent;
      backdrop-filter: none;
      pointer-events: none;
      border: none;
      align-items: center;
      overflow: hidden !important;
      visibility: hidden;
    }

    .bottom .resizer {
      top: -3px;
      bottom: auto;
      left: 0;
      right: 0;
      height: 6px;
      width: 100%;
      cursor: ns-resize;
    }

    .collapsed-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 10px;
      gap: 10px;
      cursor: pointer;
      height: 100%;
      transition: background 0.2s;
    }

    .collapsed-content:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .collapsed-icon {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #ccc;
    }

    .collapsed-icon:hover {
      background: rgba(255, 255, 255, 0.2);
      color: #fff;
    }

    .spacer {
      flex: 1;
      pointer-events: none;
    }

    dock-region {
      flex: 1;
      min-height: 0;
      width: 100%;
    }

    .center-drop-zone {
      flex: 1;
      pointer-events: none;
      min-width: 0;
      min-height: 0;
    }

    .center-drop-zone.active {
      pointer-events: auto;
    }

    .sidebar-window {
      flex: 1;
      min-height: 0;
    }

    .section-title {
      font-weight: 600;
      opacity: 0.95;
      margin: 10px 0 6px;
    }

    .divider {
      height: 1px;
      background: rgba(255, 255, 255, 0.12);
      margin: 10px 0;
    }

    .preview {
      width: 100%;
      height: 160px;
      border: 1px solid rgba(255, 255, 255, 0.12);
      background: rgba(255, 255, 255, 0.06);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    .preview img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .node-row {
      display: flex;
      gap: 10px;
      align-items: center;
      margin: 4px 0;
      font-size: 12px;
      cursor: pointer;
      user-select: none;
      padding: 3px 6px;
      border-radius: 6px;
    }

    .node-row:hover {
      background: rgba(255, 255, 255, 0.08);
    }

    .node-row.selected {
      outline: 1px solid rgba(255, 255, 255, 0.18);
      background: rgba(255, 255, 255, 0.10);
    }

    .mini-btn {
      padding: 2px 6px;
      font-size: 11px;
      line-height: 1.1;
    }

    .node-name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .row {
      display: flex;
      gap: 10px;
      align-items: center;
      margin: 6px 0;
      font-size: 12px;
    }

    .row label {
      min-width: 130px;
      opacity: 0.9;
    }

    .mono {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
      font-size: 12px;
      white-space: pre-wrap;
      opacity: 0.95;
    }

    .pill {
      display: inline-block;
      padding: 2px 6px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 999px;
      font-size: 11px;
      opacity: 0.9;
    }

    .danger {
      color: #ff8080;
    }

    .ok {
      color: #7CFC90;
    }

    .hint {
      opacity: 0.85;
      font-size: 12px;
      margin: 6px 0;
    }

    .textures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px;
      margin-top: 8px;
    }

    .tex-row {
      display: flex;
      gap: 8px;
      align-items: center;
      border: 1px solid rgba(255, 255, 255, 0.12);
      background: rgba(255, 255, 255, 0.06);
      border-radius: 8px;
      padding: 6px;
      min-width: 0;
    }

    .tex-thumb {
      width: 40px;
      height: 40px;
      border-radius: 6px;
      border: 1px solid rgba(255, 255, 255, 0.12);
      background: rgba(0, 0, 0, 0.2);
      overflow: hidden;
      flex: 0 0 auto;
    }

    .tex-thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .tex-meta {
      min-width: 0;
      flex: 1;
    }

    input[type='range'] {
      width: 180px;
    }

    input[type='text'] {
      font: inherit;
      font-size: 12px;
      padding: 6px 8px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(255, 255, 255, 0.08);
      color: #eee;
      width: 180px;
    }

    button {
      font: inherit;
      font-size: 12px;
      padding: 6px 8px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(255, 255, 255, 0.08);
      color: #eee;
      cursor: pointer;
    }

    button:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
    }

    th,
    td {
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
      padding: 4px 6px;
      text-align: left;
      vertical-align: top;
    }

    th {
      opacity: 0.9;
      font-weight: 600;
      position: sticky;
      top: 0;
      background: rgba(0, 0, 0, 0.75);
    }

    .scroll {
      overflow: auto;
      border: 1px solid rgba(255, 255, 255, 0.12);
    }

    .fill {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }

    .fill-scroll {
      flex: 1;
      min-height: 0;
      overflow: auto;
      border: 1px solid rgba(255, 255, 255, 0.12);
    }

    .card {
      border: 1px solid rgba(255, 255, 255, 0.12);
      padding: 8px;
      margin: 8px 0;
    }

    .card-title {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 6px;
    }

    /* Console panel styles */
    .console-panel {
      position: absolute;
      bottom: 10px;
      left: 10px;
      right: 10px;
      max-width: 800px;
      margin: 0 auto;
      height: 200px;
      background: rgba(15, 15, 20, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      pointer-events: auto;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 11px;
    }

    .console-header {
      padding: 6px 10px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .console-title {
      font-weight: 600;
      color: #eee;
      flex: 1;
    }

    .console-clear-btn {
      padding: 2px 8px;
      font-size: 10px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(255, 255, 255, 0.05);
      color: #aaa;
      cursor: pointer;
      border-radius: 3px;
    }

    .console-clear-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #eee;
    }

    .console-body {
      flex: 1;
      overflow-y: auto;
      padding: 6px;
    }

    .console-entry {
      padding: 2px 4px;
      border-radius: 2px;
      margin: 1px 0;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .console-entry.info {
      color: #8cf;
    }

    .console-entry.warn {
      color: #fd8;
      background: rgba(255, 200, 0, 0.08);
    }

    .console-entry.error {
      color: #f88;
      background: rgba(255, 80, 80, 0.1);
    }

    .console-time {
      opacity: 0.5;
      margin-right: 6px;
    }

    /* Help panel styles */
    .help-panel {
      position: absolute;
      top: 56px;
      left: 50%;
      transform: translateX(-50%);
      width: 500px;
      max-height: calc(100vh - 120px);
      background: rgba(20, 20, 28, 0.97);
      border: 1px solid rgba(255, 255, 255, 0.18);
      border-radius: 10px;
      padding: 16px;
      pointer-events: auto;
      overflow-y: auto;
      font-family: system-ui, -apple-system, sans-serif;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    }

    .help-title {
      font-size: 16px;
      font-weight: 600;
      color: #fff;
      margin-bottom: 16px;
      text-align: center;
    }

    .help-category {
      font-size: 12px;
      font-weight: 600;
      color: #8cf;
      margin: 14px 0 6px;
      padding-bottom: 4px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .help-row {
      display: flex;
      align-items: center;
      padding: 4px 0;
      font-size: 12px;
    }

    .help-key {
      min-width: 120px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    }

    .help-kbd {
      display: inline-block;
      padding: 2px 6px;
      background: rgba(255, 255, 255, 0.12);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      font-size: 10px;
      color: #fff;
      margin-right: 4px;
    }

    .help-desc {
      flex: 1;
      color: #ccc;
    }

    .help-close {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 24px;
      height: 24px;
      border: none;
      background: rgba(255, 255, 255, 0.1);
      color: #aaa;
      cursor: pointer;
      border-radius: 4px;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .help-close:hover {
      background: rgba(255, 255, 255, 0.2);
      color: #fff;
    }
  `;

  @state() private hudVisible = this.loadHudVisible();

  private onKeyDownBound?: (e: KeyboardEvent) => void;
  private onDragEndBound?: (_e: DragEvent) => void;
  private onDragStartBound?: (e: DragEvent) => void;
  private dockingManager = new DockingManager();
  private currentDragSourceRegion: keyof DockingLayout['regions'] | null = null;
  @state() private centerDropActive = false;

  // Panel registry: maps panel IDs to their render functions
  private panelRegistry = new Map<string, () => ReturnType<typeof html>>([
    ['debug', () => html`<debug-controls-panel></debug-controls-panel>`],
    ['map', () => html`<map-selector-panel></map-selector-panel>`],
    ['cameras', () => html`<camera-panel></camera-panel>`],
    ['post', () => html`<post-processing-panel></post-processing-panel>`],
    ['lighting', () => html`<lighting-panel></lighting-panel>`],
    ['environment', () => html`<environment-panel></environment-panel>`],
    ['scene', () => html`<explorer-panel></explorer-panel>`],
    ['console', () => html`<console-panel></console-panel>`],
    ['timeline', () => html`<animation-timeline-panel></animation-timeline-panel>`],
    ['combat', () => html`<combat-panel></combat-panel>`],
    ['states', () => html`<state-machines-panel></state-machines-panel>`],
    ['performance', () => html`<performance-panel></performance-panel>`],
    ['gametime', () => html`<game-time-panel></game-time-panel>`],
    ['behaviors', () => html`<behavior-editor-panel></behavior-editor-panel>`],
  ]);

  // Panel titles for tab headers
  private panelTitles = new Map<string, string>([
    ['debug', 'Debug'],
    ['map', 'Map'],
    ['cameras', 'Cameras'],
    ['post', 'Post-Processing'],
    ['lighting', 'Lighting'],
    ['environment', 'Environment'],
    ['scene', 'Explorer'],
    ['console', 'Console'],
    ['timeline', 'Timeline'],
    ['combat', 'Combat'],
    ['states', 'States'],
    ['performance', 'Performance'],
    ['gametime', 'Time'],
    ['behaviors', 'Behaviors'],
  ]);

  @state() private hudState: StoredHudState = this.loadState();
  @state() private dockingLayout: DockingLayout;
  @state() private currentMapIndex = 0;
  
  // Resizable sidebar state
  @state() private leftWidth = this.hudState.layout?.leftWidth ?? 440;
  @state() private rightWidth = this.hudState.layout?.rightWidth ?? 560;
  @state() private bottomHeight = 250; // Default bottom height
  @state() private leftCollapsed = this.hudState.layout?.leftCollapsed ?? false;
  @state() private rightCollapsed = this.hudState.layout?.rightCollapsed ?? false;
  @state() private bottomCollapsed = this.hudState.layout?.bottomCollapsed ?? false;
  @state() private isResizingLeft = false;
  @state() private isResizingRight = false;
  @state() private isResizingBottom = false;

  // Console log entries
  @state() private consoleLogs: UserLogEntry[] = [];
  private loggerUserCallback?: (entry: UserLogEntry) => void;
  private loggerClearCallback?: () => void;

  private onUiEventBound: (e: Event) => void;
  private onUiActionBound: (e: Event) => void;

  constructor() {
    super();
    this.dockingLayout = this.dockingManager.getLayout();
    this.onUiEventBound = (e: Event) => {
      const ce = e as CustomEvent<EngineToDebugUiEvent>;
      const msg = ce.detail;
      if (!msg) return;

      switch (msg.type) {
        case 'debugRendering:changed':
          this.hudState = {
            ...this.hudState,
            options: { ...this.hudState.options, debugRendering: msg.enabled },
          };
          this.saveState();
          return;

        case 'postprocess:pixelation:changed':
          // Note: pixelation state is intentionally NOT saved/restored
          this.hudState = { ...this.hudState, options: { ...this.hudState.options, pixelation: msg.enabled } };
          return;

        case 'postprocess:pixelSize:changed':
          this.hudState = { ...this.hudState, options: { ...this.hudState.options, pixelSize: msg.value } };
          this.saveState();
          return;

        case 'postprocess:pixelAnimate:changed':
          this.hudState = { ...this.hudState, options: { ...this.hudState.options, pixelAnimate: msg.enabled } };
          this.saveState();
          return;

        case 'postprocess:godRays:changed':
          this.hudState = { ...this.hudState, options: { ...this.hudState.options, godRays: msg.enabled } };
          this.saveState();
          return;

        case 'postprocess:chromaticAberration:changed':
          this.hudState = { ...this.hudState, options: { ...this.hudState.options, chromaticAberration: msg.enabled } };
          this.saveState();
          return;

        case 'postprocess:chromaticAberration:amount:changed':
          this.hudState = { ...this.hudState, options: { ...this.hudState.options, chromaticAberrationAmount: msg.value } };
          this.saveState();
          return;

        case 'postprocess:chromaticAberration:radialIntensity:changed':
          this.hudState = { ...this.hudState, options: { ...this.hudState.options, chromaticAberrationRadialIntensity: msg.value } };
          this.saveState();
          return;

        // scene:graph event now handled by scene-inspector-panel
        case 'scene:graph':
          // No-op: panels handle their own state
          return;
      }
    };

    this.onUiActionBound = (e: Event) => {
      const ce = e as CustomEvent<DebugUiUiAction>;
      const action = ce.detail;
      if (!action) return;

      switch (action.type) {
        case 'revealPanel': {
          this.ensureHudVisible();
          this.revealDockedPanel(action.panelId);
          return;
        }
        case 'revealSceneNode': {
          if (!action.nodeId) return;
          this.ensureHudVisible();

          // Scene inspector should live in the right dock; if floating/closed, dock it.
          this.ensurePanelDockedInRegion('scene', 'right');
          this.revealDockedPanel('scene');

          // Update selection in the inspector via engine refresh.
          DebugUiBridge.dispatchCommand({
            type: 'scene:graph:refresh',
            pinnedNodeIds: [],
            selectedNodeId: action.nodeId,
          });
          return;
        }
      }
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener(DebugUiBridge.uiEventName, this.onUiEventBound as any);
    window.addEventListener(DEBUG_UI_ACTION_EVENT, this.onUiActionBound as any);

    this.onDragStartBound = (e: DragEvent) => {
      const panelId = e.dataTransfer?.getData('text/panel-id');
      if (!panelId) return;

      this.centerDropActive = true;
      const location = this.findPanelLocation(panelId);
      this.currentDragSourceRegion = location?.regionId ?? null;
    };
    window.addEventListener('dragstart', this.onDragStartBound);

    this.onDragEndBound = () => {
      this.centerDropActive = false;
      this.currentDragSourceRegion = null;
    };
    window.addEventListener('dragend', this.onDragEndBound);

    // Restore map selection from localStorage
    this.loadMapIndexFromStorage();

    // Subscribe to logger events for console panel
    this.consoleLogs = logger.getUserBuffer();
    this.loggerUserCallback = (entry: UserLogEntry) => {
      this.consoleLogs = [...this.consoleLogs, entry];
      // Auto-scroll console to bottom
      this.requestUpdate();
    };
    this.loggerClearCallback = () => {
      this.consoleLogs = [];
    };
    loggerEventBus.on('logger:user', this.loggerUserCallback);
    loggerEventBus.on('logger:clear', this.loggerClearCallback);

    // Centralized hotkey handler
    this.onKeyDownBound = (e: KeyboardEvent) => {
      if (e.repeat) return;
      
      for (const hotkey of HOTKEY_DEFINITIONS) {
        // Check key match
        if (e.key.toLowerCase() !== hotkey.key.toLowerCase() && e.key !== hotkey.key) continue;
        
        // Check modifiers
        const needsCtrl = hotkey.modifiers?.includes('ctrl') ?? false;
        const needsShift = hotkey.modifiers?.includes('shift') ?? false;
        const needsAlt = hotkey.modifiers?.includes('alt') ?? false;
        
        if (needsCtrl !== e.ctrlKey) continue;
        if (needsShift !== e.shiftKey) continue;
        if (needsAlt !== e.altKey) continue;
        
        // Check if HUD visibility required
        if (hotkey.requiresHudVisible && !this.hudVisible) continue;
        
        // Check if specific panel required
        if (hotkey.requiresPanel && !this.hudState.panels[hotkey.requiresPanel]) continue;
        
        // Execute action
        e.preventDefault();
        hotkey.action(this);
        return;
      }
    };
    window.addEventListener('keydown', this.onKeyDownBound);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);

    // Apply persisted options to engine once the bridge is ready.
    queueMicrotask(() => {
      this.applyOptionsToEngine();
      this.applyPanelStatesToEngine();
    });
  }

  disconnectedCallback(): void {
    window.removeEventListener(DebugUiBridge.uiEventName, this.onUiEventBound as any);
    window.removeEventListener(DEBUG_UI_ACTION_EVENT, this.onUiActionBound as any);
    window.removeEventListener('keydown', this.onKeyDownBound as any);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    if (this.onDragStartBound) {
      window.removeEventListener('dragstart', this.onDragStartBound);
    }
    if (this.onDragEndBound) {
      window.removeEventListener('dragend', this.onDragEndBound);
    }
    if (this.loggerUserCallback) {
      loggerEventBus.off('logger:user', this.loggerUserCallback);
    }
    if (this.loggerClearCallback) {
      loggerEventBus.off('logger:clear', this.loggerClearCallback);
    }
    super.disconnectedCallback();
  }

  private onMouseMove = (e: MouseEvent) => {
    if (this.isResizingLeft) {
      const newWidth = Math.max(50, e.clientX);
      this.leftWidth = newWidth;
      e.preventDefault();
    } else if (this.isResizingRight) {
      const newWidth = Math.max(50, window.innerWidth - e.clientX);
      this.rightWidth = newWidth;
      e.preventDefault();
    } else if (this.isResizingBottom) {
      const newHeight = Math.max(50, window.innerHeight - e.clientY);
      this.bottomHeight = newHeight;
      e.preventDefault();
    }
  };

  private onMouseUp = () => {
    if (this.isResizingLeft || this.isResizingRight || this.isResizingBottom) {
      this.isResizingLeft = false;
      this.isResizingRight = false;
      this.isResizingBottom = false;
      
      this.hudState = {
        ...this.hudState,
        layout: {
          leftWidth: this.leftWidth,
          rightWidth: this.rightWidth,
          leftCollapsed: this.leftCollapsed,
          rightCollapsed: this.rightCollapsed,
          bottomCollapsed: this.bottomCollapsed,
        }
      };
      this.saveState();
    }
  };

  private startResizeLeft = (e: MouseEvent) => {
    this.isResizingLeft = true;
    e.preventDefault();
  };

  private startResizeRight = (e: MouseEvent) => {
    this.isResizingRight = true;
    e.preventDefault();
  };

  private startResizeBottom = (e: MouseEvent) => {
    this.isResizingBottom = true;
    e.preventDefault();
  };

  private toggleLeftCollapse = () => {
    this.leftCollapsed = !this.leftCollapsed;
    this.hudState = {
      ...this.hudState,
      layout: {
        leftWidth: this.leftWidth,
        rightWidth: this.rightWidth,
        leftCollapsed: this.leftCollapsed,
        rightCollapsed: this.rightCollapsed,
        bottomCollapsed: this.bottomCollapsed,
      }
    };
    this.saveState();
  };

  private toggleRightCollapse = () => {
    this.rightCollapsed = !this.rightCollapsed;
    this.hudState = {
      ...this.hudState,
      layout: {
        leftWidth: this.leftWidth,
        rightWidth: this.rightWidth,
        leftCollapsed: this.leftCollapsed,
        rightCollapsed: this.rightCollapsed,
        bottomCollapsed: this.bottomCollapsed,
      }
    };
    this.saveState();
  };

  private toggleBottomCollapse = () => {
    this.bottomCollapsed = !this.bottomCollapsed;
    this.hudState = {
      ...this.hudState,
      layout: {
        leftWidth: this.leftWidth,
        rightWidth: this.rightWidth,
        leftCollapsed: this.leftCollapsed,
        rightCollapsed: this.rightCollapsed,
        bottomCollapsed: this.bottomCollapsed,
      }
    };
    this.saveState();
  };

  private ensureHudVisible(): void {
    if (this.hudVisible) return;
    this.hudVisible = true;
    this.saveHudVisible();
  }

  private revealDockedPanel(panelId: string): void {
    const location = this.findPanelLocation(panelId);

    // If the panel is floating, leave it floating (it's already visible).
    const isFloating = this.dockingLayout.detachedWindows.some((w) => w.panelId === panelId);
    if (isFloating) return;

    if (!location) return;

    this.dockingManager.setRegionVisibility(location.regionId, true);
    this.dockingManager.updateActiveTab(location.regionId, location.groupIndex, panelId);
    this.dockingLayout = this.dockingManager.getLayout();
    this.requestUpdate();
  }

  private ensurePanelDockedInRegion(panelId: string, regionId: keyof DockingLayout['regions']): void {
    const location = this.findPanelLocation(panelId);
    if (location) {
      if (location.regionId !== regionId) {
        this.dockingManager.movePanel(panelId, location.regionId, regionId, location.groupIndex, 0);
      }
    } else {
      // If closed or floating, docking will either add it or move it from detached.
      this.dockingManager.dockFloatingPanel(panelId, regionId, 0);
    }

    this.dockingManager.setRegionVisibility(regionId, true);
    this.dockingManager.updateActiveTab(regionId, 0, panelId);
    this.dockingLayout = this.dockingManager.getLayout();
    this.requestUpdate();
  }

  private loadState(): StoredHudState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return structuredClone(DEFAULT_STATE);
      const parsed = JSON.parse(raw) as StoredHudState;
      if (!parsed || parsed.version !== 1) return structuredClone(DEFAULT_STATE);
      return {
        ...structuredClone(DEFAULT_STATE),
        ...parsed,
        windows: { ...structuredClone(DEFAULT_STATE.windows), ...(parsed.windows || {}) },
        panels: { ...structuredClone(DEFAULT_STATE.panels), ...(parsed.panels || {}) },
        options: { ...structuredClone(DEFAULT_STATE.options), ...(parsed.options || {}) },
        layout: { ...structuredClone(DEFAULT_STATE.layout), ...(parsed.layout || {}) },
      };
    } catch {
      return structuredClone(DEFAULT_STATE);
    }
  }

  private isPhoneOrTablet(): boolean {
    // Mirror the heuristic used for enabling the virtual gamepad overlay.
    // Goal: default HUD hidden on phones/tablets, but avoid touch laptops.
    try {
      const coarse = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
      const noHover = window.matchMedia?.('(hover: none)')?.matches ?? false;
      const small = window.matchMedia?.('(max-width: 1024px)')?.matches ?? false;
      return coarse && noHover && small;
    } catch {
      return false;
    }
  }

  private loadHudVisible(): boolean {
    // On mobile (phone/tablet), always default hidden regardless of stored setting.
    if (this.isPhoneOrTablet()) return false;
    try {
      const raw = localStorage.getItem(HUD_VISIBLE_STORAGE_KEY);
      if (raw === null) return false;
      return raw === '1' || raw === 'true';
    } catch {
      return false;
    }
  }

  private saveHudVisible(): void {
    try {
      localStorage.setItem(HUD_VISIBLE_STORAGE_KEY, this.hudVisible ? '1' : '0');
    } catch {
      // ignore
    }
  }

  private saveState(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.hudState));
    } catch {
      // ignore
    }
  }

  private applyOptionsToEngine(): void {
    const o = this.hudState.options;
    DebugUiBridge.dispatchCommand({ type: 'debugRendering:set', enabled: o.debugRendering });
    DebugUiBridge.dispatchCommand({ type: 'splineDebug:set', enabled: o.splineDebug });
    DebugUiBridge.dispatchCommand({ type: 'playerDebug:set', enabled: o.playerDebug });
    DebugUiBridge.dispatchCommand({ type: 'weaponMountDebug:set', enabled: o.weaponMountDebug });

    // Note: pixelation is intentionally NOT restored - always uses engine default (off)
    DebugUiBridge.dispatchCommand({ type: 'postprocess:pixelSize:set', value: o.pixelSize });
    DebugUiBridge.dispatchCommand({ type: 'postprocess:pixelAnimate:set', enabled: o.pixelAnimate });
    DebugUiBridge.dispatchCommand({ type: 'postprocess:godRays:set', enabled: o.godRays });
    DebugUiBridge.dispatchCommand({ type: 'postprocess:chromaticAberration:set', enabled: o.chromaticAberration });
    DebugUiBridge.dispatchCommand({ type: 'postprocess:chromaticAberration:amount:set', value: o.chromaticAberrationAmount });
    DebugUiBridge.dispatchCommand({ type: 'postprocess:chromaticAberration:radialIntensity:set', value: o.chromaticAberrationRadialIntensity });

    DebugUiBridge.dispatchCommand({ type: 'lighting:fullBright:set', enabled: o.fullBright });
  }

  private applySingleOptionToEngine<K extends keyof StoredHudState['options']>(
    key: K,
    value: StoredHudState['options'][K]
  ): void {
    switch (key) {
      case 'debugRendering':
        DebugUiBridge.dispatchCommand({ type: 'debugRendering:set', enabled: value as boolean });
        return;
      case 'splineDebug':
        DebugUiBridge.dispatchCommand({ type: 'splineDebug:set', enabled: value as boolean });
        return;
      case 'playerDebug':
        DebugUiBridge.dispatchCommand({ type: 'playerDebug:set', enabled: value as boolean });
        return;
      case 'weaponMountDebug':
        DebugUiBridge.dispatchCommand({ type: 'weaponMountDebug:set', enabled: value as boolean });
        return;

      case 'pixelation':
        // Note: pixelation is intentionally NOT restored - always uses engine default (off)
        return;
      case 'pixelSize':
        DebugUiBridge.dispatchCommand({ type: 'postprocess:pixelSize:set', value: value as number });
        return;
      case 'pixelAnimate':
        DebugUiBridge.dispatchCommand({ type: 'postprocess:pixelAnimate:set', enabled: value as boolean });
        return;
      case 'godRays':
        DebugUiBridge.dispatchCommand({ type: 'postprocess:godRays:set', enabled: value as boolean });
        return;

      case 'chromaticAberration':
        DebugUiBridge.dispatchCommand({ type: 'postprocess:chromaticAberration:set', enabled: value as boolean });
        return;

      case 'chromaticAberrationAmount':
        DebugUiBridge.dispatchCommand({ type: 'postprocess:chromaticAberration:amount:set', value: value as number });
        return;

      case 'chromaticAberrationRadialIntensity':
        DebugUiBridge.dispatchCommand({ type: 'postprocess:chromaticAberration:radialIntensity:set', value: value as number });
        return;

      case 'fullBright':
        DebugUiBridge.dispatchCommand({ type: 'lighting:fullBright:set', enabled: value as boolean });
        return;
    }
  }

  private applyPanelStatesToEngine(): void {
    // Sync timeline panel state to engine (AnimationDebugOverlay)
    DebugUiBridge.dispatchCommand({ type: 'timeline:set', enabled: this.hudState.panels.timeline });
  }

  private setOption<K extends keyof StoredHudState['options']>(key: K, value: StoredHudState['options'][K]): void {
    this.hudState = {
      ...this.hudState,
      options: {
        ...this.hudState.options,
        [key]: value,
      },
    };
    this.saveState();
    // Avoid re-applying unrelated options (e.g. post-process) when toggling debug geometry.
    this.applySingleOptionToEngine(key, value);
  }

  private toggleBool(key: keyof StoredHudState['options']): void {
    const current = this.hudState.options[key] as unknown as boolean;
    this.setOption(key as any, (!current) as any);
  }

  // === Drag-and-drop handlers ===

  private handleTabDragStart(e: CustomEvent): void {
    const { panelId } = e.detail;

    this.centerDropActive = true;
    
    // Find which region this panel is in
    const layout = this.dockingLayout.regions;
    for (const [regionKey, region] of Object.entries(layout)) {
      for (let groupIndex = 0; groupIndex < region.tabGroups.length; groupIndex++) {
        const group = region.tabGroups[groupIndex];
        if (group && group.tabs.includes(panelId)) {
          this.currentDragSourceRegion = regionKey as keyof DockingLayout['regions'];
          break;
        }
      }
      if (this.currentDragSourceRegion) break;
    }
  }

  private findPanelLocation(panelId: string): { regionId: keyof DockingLayout['regions']; groupIndex: number } | null {
    for (const [regionKey, region] of Object.entries(this.dockingLayout.regions)) {
      for (let groupIndex = 0; groupIndex < region.tabGroups.length; groupIndex++) {
        const group = region.tabGroups[groupIndex];
        if (group && group.tabs.includes(panelId)) {
          return { regionId: regionKey as keyof DockingLayout['regions'], groupIndex };
        }
      }
    }
    return null;
  }

  private handleCenterDragOver = (e: DragEvent) => {
    // Accept drops to convert docked panels into floating windows.
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  };

  private handleCenterDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const panelId = e.dataTransfer?.getData('text/panel-id');
    if (!panelId) return;

    // If the panel is currently docked, remove it from its region.
    const location = this.findPanelLocation(panelId);
    if (location) {
      this.dockingManager.removePanel(location.regionId, location.groupIndex, panelId);
    }

    // Create a floating window centered around the drop point.
    const width = 600;
    const height = 400;
    this.dockingManager.addFloatingPanel(panelId, {
      x: Math.max(20, Math.round(e.clientX - width / 2)),
      y: Math.max(20, Math.round(e.clientY - height / 2)),
      width,
      height,
    });

    this.dockingLayout = this.dockingManager.getLayout();
    this.requestUpdate();

    this.currentDragSourceRegion = null;
    this.centerDropActive = false;
  };

  private handlePanelDrop(e: CustomEvent): void {
    const { panelId, targetRegion } = e.detail;
    
    if (!this.currentDragSourceRegion || !panelId) {
      logger.ui.warn('Invalid drag state');
      return;
    }

    // Don't move if dropping in same region
    if (this.currentDragSourceRegion === targetRegion) {
      this.currentDragSourceRegion = null;
      return;
    }

    // Move panel using DockingManager
    const success = this.dockingManager.movePanel(
      panelId,
      this.currentDragSourceRegion,
      targetRegion
    );

    if (success) {
      // Update layout and re-render
      this.dockingLayout = this.dockingManager.getLayout();
      this.requestUpdate();
    }

    // Reset drag state
    this.currentDragSourceRegion = null;
    this.centerDropActive = false;
  }

  private handleFloatingClose(panelId: string): void {
    this.dockingManager.removeFloatingPanel(panelId);
    this.dockingLayout = this.dockingManager.getLayout();
    this.requestUpdate();
  }

  private handleFloatingMove(panelId: string, position: { x: number; y: number }): void {
    this.dockingManager.updateFloatingPanelBounds(panelId, position);
    this.dockingLayout = this.dockingManager.getLayout();
  }

  private handleFloatingResize(panelId: string, size: { width: number; height: number }): void {
    this.dockingManager.updateFloatingPanelBounds(panelId, size);
    this.dockingLayout = this.dockingManager.getLayout();
  }

  private handleFloatingDragStart(_panelId: string): void {
    // Store which panel is being dragged so dock regions can accept it
    this.currentDragSourceRegion = null;
    // The drag data will be set by himum-debug-window component
  }

  private handleFloatingDragEnd(panelId: string, point: { x: number; y: number }): void {
    // Determine if the drag ended over a dock region.
    const regionEls = Array.from(this.shadowRoot?.querySelectorAll('dock-region') ?? []) as HTMLElement[];
    const hit = regionEls.find((el) => {
      const rect = el.getBoundingClientRect();
      return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
    });

    const targetRegion = (hit as any)?.regionId as keyof DockingLayout['regions'] | undefined;
    if (!targetRegion || !['left', 'right', 'top', 'bottom'].includes(targetRegion)) return;

    this.dockingManager.dockFloatingPanel(panelId, targetRegion);
    this.dockingLayout = this.dockingManager.getLayout();
    this.requestUpdate();
  }

  /**
   * Render tabs dynamically based on layout data
   */
  private renderTabsForRegion(regionId: keyof DockingLayout['regions'], groupIndex = 0) {
    const region = this.dockingLayout.regions[regionId];
    const group = region.tabGroups[groupIndex];
    
    if (!group) return nothing;

    return group.tabs.map(panelId => {
      const renderPanel = this.panelRegistry.get(panelId);
      const title = this.panelTitles.get(panelId) || panelId;
      
      if (!renderPanel) {
        logger.ui.warn(`Unknown panel: ${panelId}`);
        return nothing;
      }

      return html`
        <dock-tab tab-id="${panelId}" title="${title}">
          ${renderPanel()}
        </dock-tab>
      `;
    });
  }

  // === Public API for hotkey actions ===
  
  /**
   * Get reference to animation timeline panel
   */
  getTimelinePanel(): HTMLElement | null {
    return this.shadowRoot?.querySelector('animation-timeline-panel') || null;
  }
  
  toggleHud(): void {
    this.hudVisible = !this.hudVisible;
    this.saveHudVisible();
  }

  togglePanel(panel: PanelId): void {
    const newState = !this.hudState.panels[panel];
    this.hudState = {
      ...this.hudState,
      panels: {
        ...this.hudState.panels,
        [panel]: newState,
      },
    };
    this.saveState();
    
    // Special handling for timeline panel - sync AnimationDebugOverlay state
    if (panel === 'timeline') {
      DebugUiBridge.dispatchCommand({ type: 'timeline:set', enabled: newState });
    }
  }

  toggleDockRegion(regionId: 'left' | 'right' | 'top' | 'bottom'): void {
    this.dockingManager.toggleRegionVisibility(regionId);
    this.dockingLayout = this.dockingManager.getLayout();
    this.requestUpdate();
  }

  /**
   * Get list of panels that are not currently docked or floating
   */
  private getClosedPanels(): Array<{ id: string; title: string }> {
    const allPanels = Array.from(this.panelRegistry.keys());
    const activePanels = new Set<string>();

    // Collect all active panels from dock regions
    Object.values(this.dockingLayout.regions).forEach(region => {
      region.tabGroups.forEach(group => {
        group.tabs.forEach(tabId => activePanels.add(tabId));
      });
    });

    // Collect all floating panels
    this.dockingLayout.detachedWindows.forEach(win => {
      activePanels.add(win.panelId);
    });

    // Return panels that aren't active anywhere
    return allPanels
      .filter(id => !activePanels.has(id))
      .map(id => ({
        id,
        title: this.panelTitles.get(id) || id
      }));
  }

  /**
   * Open a closed panel as a floating window in the center of the screen
   */
  private openPanelAsFloating(panelId: string): void {
    // Calculate center position
    const centerX = window.innerWidth / 2 - 300;
    const centerY = window.innerHeight / 2 - 200;

    this.dockingManager.addFloatingPanel(panelId, {
      x: Math.max(50, centerX),
      y: Math.max(50, centerY),
      width: 600,
      height: 400
    });
    
    this.dockingLayout = this.dockingManager.getLayout();
    this.requestUpdate();
  }

  toggleDebugRendering(): void {
    this.toggleBool('debugRendering');
  }

  /**
   * Toggle game pause state via gameTime
   */
  toggleGamePause(): void {
    const gt = (window as any).__himumTestHooks?.gameTime;
    if (gt) {
      gt.togglePause();
    }
  }

  /**
   * Toggle slow-motion mode via gameTime
   * If currently slow (<0.5x), return to normal speed.
   * Otherwise, enter slow-mo at 0.2x.
   */
  toggleSlowMotion(): void {
    const gt = (window as any).__himumTestHooks?.gameTime;
    if (!gt) return;
    
    const state = gt.getState();
    if (state.timeScale < 0.5 && !state.isPaused) {
      // Currently in slow-mo, return to normal
      gt.normalSpeed(0.3);
    } else {
      // Enter slow-mo
      gt.slowMotion(0.2, 0.3);
    }
  }

  // Map selector methods
  private get availableMaps() {
    return availableMaps;
  }

  private loadMapIndexFromStorage = () => {
    const savedMapPath = localStorage.getItem('himum-selected-map');
    if (savedMapPath) {
      const maps = this.availableMaps;
      const index = maps.findIndex(m => m.path === savedMapPath);
      if (index !== -1) {
        this.currentMapIndex = index;
      }
    }
  };

  private getCurrentMapName(): string {
    const maps = this.availableMaps;
    if (maps.length === 0) return 'No maps';
    const current = maps[this.currentMapIndex];
    return current.path;
  }

  private handleMapSelection = (e: Event) => {
    const select = e.target as HTMLSelectElement;
    const selectedPath = select.value;
    
    // Update current index
    const maps = this.availableMaps;
    this.currentMapIndex = maps.findIndex(m => m.path === selectedPath);
    if (this.currentMapIndex === -1) this.currentMapIndex = 0;
    
    // Save to localStorage
    localStorage.setItem('himum-selected-map', selectedPath);
    
    this.changeMap();
  };

  private reloadMap = () => {
    const maps = this.availableMaps;
    if (maps.length === 0) return;
    this.changeMap();
  };

  private changeMap() {
    const maps = this.availableMaps;
    const map = maps[this.currentMapIndex];
    DebugUiBridge.dispatchCommand({ type: 'map:change', path: map.path });
  }

  render() {
    if (!this.hudVisible) return nothing;
    
    const layout = this.dockingLayout;
    const panels = this.hudState.panels; // Keep for backwards compatibility with help panel
    
    return html`
      <!-- Top bar with dock region and panel controls -->
      <div class="top-bar" aria-hidden="true">
        <span class="top-bar-title">Debug HUD</span>
        <div class="top-bar-divider"></div>
        
        <!-- Map Selector inline -->
        <div class="map-selector-inline">
          <span class="map-label">🗺️</span>
          <select 
            class="map-dropdown"
            @change=${this.handleMapSelection}
            .value=${this.getCurrentMapName()}
          >
            ${this.availableMaps.map(map => html`
              <option value=${map.path} ?selected=${map.path === this.getCurrentMapName()}>
                ${map.filename}
              </option>
            `)}
          </select>
          <button 
            class="map-reload-btn"
            @click=${this.reloadMap}
            title="Reload current map"
          >↻</button>
        </div>
        
        <div class="top-bar-divider"></div>
        
        <!-- Closed Panels -->
        ${this.getClosedPanels().length > 0 ? html`
          <span class="top-bar-section-label">Panels:</span>
          ${this.getClosedPanels().map(panel => html`
            <button 
              class="panel-btn"
              @click=${() => this.openPanelAsFloating(panel.id)}
              title="Open ${panel.title} as floating window"
            >
              ${panel.title}
            </button>
          `)}
        ` : nothing}
        
        <div class="top-bar-spacer"></div>
        <span class="top-bar-hint">F8 to hide HUD</span>
      </div>

      <!-- Main dock layout -->
      <div
        class="dock"
        style="bottom: ${layout.regions.bottom.visible && !this.bottomCollapsed ? `${this.bottomHeight}px` : '0'};"
        aria-hidden="true"
      >
        <!-- Left Expand Button -->
        ${this.leftCollapsed ? html`
          <div class="expand-btn left-expand" @click=${this.toggleLeftCollapse} title="Expand Sidebar">
            »
          </div>
        ` : nothing}

        <!-- LEFT DOCK (Sidebar) -->
        <div 
          class="left ${this.leftCollapsed ? 'collapsed' : ''}" 
          style="--left-width: ${this.leftWidth}px"
        >
          ${!this.leftCollapsed
            ? html`
                <dock-region
                  id="left"
                  regionId="left"
                  orientation="horizontal"
                  .size=${this.leftWidth}
                  @panel-drop=${this.handlePanelDrop}
                  @expand=${() => {
                    const tabGroup = this.shadowRoot?.querySelector('#left dock-tab-group') as any;
                    if (tabGroup) {
                      tabGroup.collapsed = false;
                      tabGroup.classList.remove('collapsed');
                    }
                  }}
                >
                  <dock-tab-group
                    .region=${'left'}
                    .activeTab=${this.dockingLayout.regions.left.tabGroups[0]?.activeTab || ''}
                    @tab-changed=${(e: CustomEvent) => {
                      this.dockingManager.updateActiveTab('left', 0, e.detail.tabId);
                      this.dockingLayout = this.dockingManager.getLayout();
                      this.requestUpdate();
                    }}
                    @tab-close=${(e: CustomEvent) => {
                      this.dockingManager.removePanel('left', 0, e.detail.tabId);
                      this.dockingLayout = this.dockingManager.getLayout();
                      this.requestUpdate();
                    }}
                    @tab-drag-start=${this.handleTabDragStart}
                    @region-collapse=${(e: CustomEvent) => {
                      // Sync tab group collapse with sidebar collapse
                      if (e.detail.collapsed) {
                        this.toggleLeftCollapse();
                      }
                    }}
                  >
                    ${this.renderTabsForRegion('left', 0)}
                  </dock-tab-group>
                </dock-region>
                <div class="resizer" @mousedown=${this.startResizeLeft}></div>
              `
            : nothing
          }
        </div>

        <div
          class="center-drop-zone ${this.centerDropActive ? 'active' : ''}"
          @dragover=${this.handleCenterDragOver}
          @drop=${this.handleCenterDrop}
        ></div>

        <!-- RIGHT DOCK (Scene Inspector) -->
        <div 
          class="right ${this.rightCollapsed ? 'collapsed' : ''}"
          style="--right-width: ${this.rightWidth}px"
        >
          ${!this.rightCollapsed
            ? html`
                <div class="resizer" @mousedown=${this.startResizeRight}></div>
                <dock-region
                  id="right"
                  regionId="right"
                  orientation="horizontal"
                  .size=${this.rightWidth}
                  @panel-drop=${this.handlePanelDrop}
                  @expand=${() => {
                    const tabGroup = this.shadowRoot?.querySelector('#right dock-tab-group') as any;
                    if (tabGroup) {
                      tabGroup.collapsed = false;
                      tabGroup.classList.remove('collapsed');
                    }
                  }}
                >
                  <dock-tab-group
                    .region=${'right'}
                    .activeTab=${this.dockingLayout.regions.right.tabGroups[0]?.activeTab || ''}
                    @tab-changed=${(e: CustomEvent) => {
                      this.dockingManager.updateActiveTab('right', 0, e.detail.tabId);
                      this.dockingLayout = this.dockingManager.getLayout();
                      this.requestUpdate();
                    }}
                    @tab-close=${(e: CustomEvent) => {
                      this.dockingManager.removePanel('right', 0, e.detail.tabId);
                      this.dockingLayout = this.dockingManager.getLayout();
                      this.requestUpdate();
                    }}
                    @tab-drag-start=${this.handleTabDragStart}
                    @region-collapse=${(e: CustomEvent) => {
                      // Sync tab group collapse with sidebar collapse
                      if (e.detail.collapsed) {
                        this.toggleRightCollapse();
                      }
                    }}
                  >
                    ${this.renderTabsForRegion('right', 0)}
                  </dock-tab-group>
                </dock-region>
              `
            : nothing
          }
        </div>
        
        <!-- Right Expand Button -->
        ${this.rightCollapsed ? html`
          <div class="expand-btn right-expand" @click=${this.toggleRightCollapse} title="Expand Sidebar">
            «
          </div>
        ` : nothing}
      </div>

      <!-- BOTTOM DOCK (250px) - Console Panel -->
      ${layout.regions.bottom.visible ? html`
        <!-- Bottom Expand Button -->
        ${this.bottomCollapsed ? html`
          <div class="expand-btn bottom-expand" @click=${this.toggleBottomCollapse} title="Expand Bottom Panel">
            <span style="transform: rotate(90deg); display: inline-block;">«</span>
          </div>
        ` : nothing}
        
        <div 
          class="bottom ${this.bottomCollapsed ? 'collapsed' : ''}"
          style="--bottom-height: ${this.bottomHeight}px"
          aria-hidden="true"
        >
          ${!this.bottomCollapsed
            ? html`
                <div class="resizer" @mousedown=${this.startResizeBottom}></div>
                <dock-region
                  id="bottom"
                  regionId="bottom"
                  orientation="vertical"
                  .size=${this.bottomHeight}
                  .resizable=${false}
                  @panel-drop=${this.handlePanelDrop}
                  @expand=${() => {
                    const tabGroup = this.shadowRoot?.querySelector('#bottom dock-tab-group') as any;
                    if (tabGroup) {
                      tabGroup.collapsed = false;
                      tabGroup.classList.remove('collapsed');
                    }
                  }}
                >
                  <dock-tab-group
                    .region=${'bottom'}
                    .activeTab=${this.dockingLayout.regions.bottom.tabGroups[0]?.activeTab || ''}
                    @tab-changed=${(e: CustomEvent) => {
                      this.dockingManager.updateActiveTab('bottom', 0, e.detail.tabId);
                      this.dockingLayout = this.dockingManager.getLayout();
                      this.requestUpdate();
                    }}
                    @tab-close=${(e: CustomEvent) => {
                      this.dockingManager.removePanel('bottom', 0, e.detail.tabId);
                      this.dockingLayout = this.dockingManager.getLayout();
                      this.requestUpdate();
                    }}
                    @tab-drag-start=${this.handleTabDragStart}
                    @region-collapse=${(e: CustomEvent) => {
                      // Sync tab group collapse with sidebar collapse
                      if (e.detail.collapsed) {
                        this.toggleBottomCollapse();
                      }
                    }}
                  >
                    ${this.renderTabsForRegion('bottom', 0)}
                  </dock-tab-group>
                </dock-region>
              `
            : nothing
          }
        </div>
      ` : nothing}

      <!-- Floating panels (detached windows) -->
      ${this.dockingLayout.detachedWindows.map(window => {
        const renderPanel = this.panelRegistry.get(window.panelId);
        const title = this.panelTitles.get(window.panelId) || window.panelId;
        
        if (!renderPanel) return nothing;
        
        return html`
          <himum-debug-window
            .title=${title}
            .initialX=${window.bounds.x}
            .initialY=${window.bounds.y}
            .initialWidth=${window.bounds.width}
            .initialHeight=${window.bounds.height}
            @window-close=${() => this.handleFloatingClose(window.panelId)}
            @window-move=${(e: CustomEvent) => this.handleFloatingMove(window.panelId, e.detail)}
            @window-resize=${(e: CustomEvent) => this.handleFloatingResize(window.panelId, e.detail)}
            @drag-start=${() => this.handleFloatingDragStart(window.panelId)}
            @drag-end=${(e: CustomEvent) => this.handleFloatingDragEnd(window.panelId, e.detail)}
          >
            ${renderPanel()}
          </himum-debug-window>
        `;
      })}

      <!-- Help panel (floating modal) -->
      ${panels.help ? this.renderHelpPanel() : nothing}
    `;
  }

  private renderHelpPanel() {
    // Group hotkeys by category
    const categories = new Map<string, Array<{ key: string; modifiers?: string[]; description: string }>>();
    
    for (const h of HOTKEY_DEFINITIONS) {
      const cat = h.category;
      if (!categories.has(cat)) categories.set(cat, []);
      categories.get(cat)!.push({ key: h.key, modifiers: h.modifiers, description: h.description });
    }
    
    // Add external hotkey docs
    for (const h of EXTERNAL_HOTKEY_DOCS) {
      const cat = h.category;
      if (!categories.has(cat)) categories.set(cat, []);
      categories.get(cat)!.push({ key: h.key, modifiers: h.modifiers, description: h.description });
    }
    
    return html`
      <div class="help-panel">
        <button class="help-close" @click=${() => this.togglePanel('help')}>×</button>
        <div class="help-title">Keyboard Shortcuts</div>
        
        ${Array.from(categories.entries()).map(([category, hotkeys]) => html`
          <div class="help-category">${category}</div>
          ${hotkeys.map(h => html`
            <div class="help-row">
              <div class="help-key">
                ${h.modifiers?.map(m => html`<span class="help-kbd">${m}</span>`) ?? nothing}
                <span class="help-kbd">${h.key}</span>
              </div>
              <div class="help-desc">${h.description}</div>
            </div>
          `)}
        `)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'himum-debug-hud': HimumDebugHud;
  }
}
